import { Link } from 'react-router-dom';

/** Two letters for a missing picture: the first letters of the brand, else of the title. */
function initials(text: string): string {
  return (
    text
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]!.toUpperCase())
      .join('') || '?'
  );
}

/** The prototype's product thumbnail (`.im`): the catalog's picture, or initials on ink. */
export function Thumb({
  image,
  fallback,
  size = 34,
}: {
  image: string | null;
  fallback: string;
  size?: number;
}) {
  const box = { width: size, height: size };
  return image ? (
    <span
      className="border-line inline-grid flex-none place-items-center overflow-hidden rounded-[7px] border bg-[#F1F4F8] p-0.5"
      style={box}
    >
      <img src={image} alt="" loading="lazy" className="h-full w-full object-contain" />
    </span>
  ) : (
    <span
      className="bg-ink2 font-display grid flex-none place-items-center rounded-[7px] text-[11px] font-semibold text-white"
      style={box}
      aria-hidden="true"
    >
      {initials(fallback)}
    </span>
  );
}

interface ProductCellProps {
  image: string | null;
  title: string;
  /** The grey line under the title (category › family, or the item code). */
  sub?: string;
  /** Monospace sub line, as the prototype's variant rows show the SKU. */
  mono?: boolean;
  to?: string;
}

/** The prototype's `.pn`: thumbnail, a bold one-line title and a grey sub line; a link when `to` is set. */
export function ProductCell({ image, title, sub, mono = false, to }: ProductCellProps) {
  const body = (
    <>
      <Thumb image={image} fallback={title} />
      <span className="min-w-0">
        <b
          className={`block max-w-[300px] truncate font-semibold ${to ? 'group-hover:text-blue' : ''}`}
          title={title}
        >
          {title}
        </b>
        {sub ? (
          <span
            className={`text-muted block max-w-[300px] truncate text-[11.5px] ${mono ? 'font-mono' : ''}`}
          >
            {sub}
          </span>
        ) : null}
      </span>
    </>
  );
  return to ? (
    <Link to={to} className="group flex min-w-0 items-center gap-[9px]">
      {body}
    </Link>
  ) : (
    <span className="flex min-w-0 items-center gap-[9px]">{body}</span>
  );
}
